import { Card, CardContent } from "../ui/card";
import { Wrench, Building2, FileSignature, UserRound, ShieldCheck, FileText, PackageOpen } from "lucide-react";

const ACCENTS = {
  purple: { border: "hover:border-[#655dc6] focus-visible:border-[#655dc6]", iconBg: "bg-[#655dc6]/10", iconHover: "group-hover:bg-[#655dc6]", icon: "text-[#655dc6]" },
  emerald: { border: "hover:border-emerald-600 focus-visible:border-emerald-600", iconBg: "bg-emerald-600/10", iconHover: "group-hover:bg-emerald-600", icon: "text-emerald-600" },
  sky: { border: "hover:border-sky-600 focus-visible:border-sky-600", iconBg: "bg-sky-600/10", iconHover: "group-hover:bg-sky-600", icon: "text-sky-600" },
  amber: { border: "hover:border-amber-600 focus-visible:border-amber-600", iconBg: "bg-amber-600/10", iconHover: "group-hover:bg-amber-600", icon: "text-amber-600" },
};

const ServiceTile = ({ onClick, icon: Icon, label, testId, accent = "purple", row = false }) => {
  const a = ACCENTS[accent];
  return (
    <button
      onClick={onClick}
      className={`group bg-white border-2 border-slate-200 ${a.border} hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] rounded-2xl p-4 sm:p-5 transition-all duration-300 outline-none w-full
        flex items-center justify-center gap-3
        ${row ? "flex-row sm:flex-col text-start sm:text-center min-h-[76px] sm:min-h-[150px]" : "flex-col text-center min-h-[124px] sm:min-h-[150px]"}`}
      data-testid={testId}
    >
      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full ${a.iconBg} ${a.iconHover} flex items-center justify-center transition-colors shrink-0`}>
        <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${a.icon} group-hover:text-white transition-colors`} />
      </div>
      <h3 className={`text-sm sm:text-[15px] font-bold text-slate-800 leading-snug break-words ${row ? "flex-1 sm:flex-none" : "w-full"}`}>
        {label}
      </h3>
    </button>
  );
};

// Chooser shown when an OFFICE/FACILITY has extra QR services enabled.
export const OfficeServiceChooser = ({ scannedLocation, lang, showTreatmentPlan = true, showMedicalReport = false, onMaintenance, onTreatmentPlan, onConsents, onDoctors, onMedicalReport }) => {
  const tiles = [
    { onClick: onMaintenance, icon: Wrench, testId: "choice-maintenance-btn", label: lang === "ar" ? "بلاغ صيانة" : "Maintenance Report" },
    ...(showTreatmentPlan ? [
      { onClick: onTreatmentPlan, icon: Building2, testId: "choice-treatment-plan-btn", label: lang === "ar" ? "الخطة العلاجية" : "Treatment Plan" },
      { onClick: onConsents, icon: FileSignature, testId: "choice-consents-btn", label: lang === "ar" ? "الإقرارات" : "Consents" },
      { onClick: onDoctors, icon: UserRound, testId: "choice-doctors-btn", label: lang === "ar" ? "أطباء المركز" : "Center Doctors" },
    ] : []),
    ...(showMedicalReport ? [
      { onClick: onMedicalReport, icon: FileText, accent: "sky", testId: "choice-medical-report-btn", label: lang === "ar" ? "طلب تقرير طبي" : "Medical Report Request" },
    ] : []),
  ];
  return (
    <Card className="border-0 shadow-sm max-w-3xl mx-auto">
      <CardContent className="py-8 sm:py-10 px-4 sm:px-8 space-y-6">
        <div className="text-center">
          <h2
            dir="auto"
            style={{ unicodeBidi: "plaintext" }}
            className="text-lg sm:text-xl font-bold text-[#4a3fa8] mb-1 leading-relaxed"
            data-testid="chooser-location-name"
          >
            {scannedLocation.name}
          </h2>
          <p className="text-slate-500 text-sm">
            {lang === "ar" ? "يرجى اختيار الخدمة المطلوبة" : "Please choose a service"}
          </p>
        </div>
        <div className={`grid grid-cols-2 ${tiles.length > 2 ? "lg:grid-cols-4" : "max-w-md mx-auto"} gap-3 sm:gap-4`}>
          {tiles.map((tile) => <ServiceTile key={tile.testId} {...tile} />)}
        </div>
      </CardContent>
    </Card>
  );
};

// Chooser shown for CLINIC scans: pick Maintenance vs Sterilization vs Medical Report.
export const ClinicServiceChooser = ({ isRTL, onPickMaintenance, onPickSterilization, onPickMedicalReport, onPickInstruments }) => (
  <Card className="border-0 shadow-sm" data-testid="clinic-service-chooser">
    <CardContent className="py-6 px-4 sm:px-6">
      <p className="text-center text-slate-600 mb-4 font-medium">
        {isRTL ? "اختر نوع الطلب" : "Choose request type"}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <ServiceTile row onClick={onPickMaintenance} icon={Wrench} testId="pick-maintenance-btn"
          label={isRTL ? "طلب صيانة" : "Maintenance Request"} />
        <ServiceTile row onClick={onPickSterilization} icon={ShieldCheck} accent="emerald" testId="pick-sterilization-btn"
          label={isRTL ? "طلب تعقيم" : "Sterilization Request"} />
        <ServiceTile row onClick={onPickMedicalReport} icon={FileText} accent="sky" testId="pick-medical-report-btn"
          label={isRTL ? "طلب تقرير طبي" : "Medical Report Request"} />
        <ServiceTile row onClick={onPickInstruments} icon={PackageOpen} accent="amber" testId="pick-instruments-btn"
          label="Request Instruments" />
      </div>
    </CardContent>
  </Card>
);
